import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import API from '@beardeddevops/react.api';
import Framework from '@beardeddevops/react.framework';
import moment from 'moment-timezone';
import Types from 'constants/Types';
import {Objects} from 'components';
import {notifyHelpdeskContactedUpdated} from 'components/Header/Notifications';
import {
	buildHelpdeskCallQueue,
	clearHelpdeskCallSnooze,
	formatPhoneNumber,
	getCallRowKey,
	getEngineerName,
	getEngineerPhone,
	getEngineerPkey,
	getLastContacted,
	seesHelpdeskCallBanner,
} from './helpdeskCallQueue';

const displayEngineerName = (notification) => {
	const name = getEngineerName(notification);
	return name.startsWith('Call ') ? name.slice(5) : name;
};

const contactedRowPkey = (row) => {
	const pkey = Number(row?.engineer_pkey ?? row?.fe_pkey ?? row?.pkey);
	return Number.isFinite(pkey) && pkey > 0 ? pkey : null;
};

const contactedRowPhone = (row) => String(row?.phone_cell ?? row?.phone_number ?? row?.phone ?? '').trim();

const contactedRowName = (row) => {
	const first = row?.fe_name_first ?? row?.engineer_name_first ?? '';
	const last = row?.fe_name_last ?? row?.engineer_name_last ?? '';
	return `${first} ${last}`.trim().toLowerCase();
};

const displayLastContacted = (notification) => {
	const lastContacted = getLastContacted(notification);
	if (lastContacted) {
		return moment(lastContacted).format('MMM D, YYYY h:mm A');
	}
	return 'Never contacted';
};

/** Ticket/customer tabs use history.push("#tab"); that must not count as a new page. */
const helpdeskCallRouteSignature = (location) => {
	if (!location) {
		return '';
	}
	return `${location.pathname || ''}${location.search || ''}`;
};

/**
 * Interrupts on load and on each navigation while type-9 call reminders remain.
 * Dismiss lasts only until the next route change. Banner snooze does not hide this list.
 */
const HelpdeskCallModal = ({notifications = [], history, onSessionCheck, roles, mainRole}) => {
	const eligible = seesHelpdeskCallBanner(roles, mainRole);
	const queue = useMemo(
		() => (eligible ? buildHelpdeskCallQueue(notifications) : []),
		[eligible, notifications]
	);
	const queueEngineerKey = queue
		.map((item) => getEngineerPkey(item))
		.filter((pkey) => pkey != null && !Number.isNaN(pkey))
		.join(",");
	const [open, setOpen] = useState(false);
	const [logCallOpen, setLogCallOpen] = useState(false);
	const [activeEngineer, setActiveEngineer] = useState(null);
	const [phonesByEngineer, setPhonesByEngineer] = useState({});
	const [phonesByName, setPhonesByName] = useState({});
	const hadQueueRef = useRef(false);
	const queueCountRef = useRef(0);
	const eligibleRef = useRef(false);
	const routeSignatureRef = useRef(
		helpdeskCallRouteSignature(history?.location)
	);

	queueCountRef.current = queue.length;
	eligibleRef.current = eligible;

	useEffect(() => {
		if (!eligible || queue.length === 0) {
			setOpen(false);
			hadQueueRef.current = false;
			return;
		}
		if (!hadQueueRef.current) {
			setOpen(true);
		}
		hadQueueRef.current = true;
	}, [eligible, queue.length]);

	useEffect(() => {
		if (!eligible || queue.length === 0) {
			return undefined;
		}
		let cancelled = false;
		(async () => {
			try {
				const model = new API.BuildClass(Types.Help_Desks_Contacted._name, 'GET_VIEW');
				model.limit = 500;
				model.filter = {
					filterDate: moment.tz('America/Phoenix').format('YYYY-MM-DD'),
				};
				const data = await model.submit(null, 'GET_VIEW', true);
				const rows = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
				if (cancelled) {
					return;
				}
				const byPkey = {};
				const byName = {};
				rows.forEach((row) => {
					const phone = contactedRowPhone(row);
					if (!phone) {
						return;
					}
					const pkey = contactedRowPkey(row);
					if (pkey != null) {
						byPkey[pkey] = phone;
					}
					const name = contactedRowName(row);
					if (name) {
						byName[name] = phone;
					}
				});
				setPhonesByEngineer(byPkey);
				setPhonesByName(byName);
			} catch (e) {
				// The column stays blank until the contacted list can be read.
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [eligible, queueEngineerKey]);

	useEffect(() => {
		if (!history || typeof history.listen !== 'function') {
			return undefined;
		}
		routeSignatureRef.current = helpdeskCallRouteSignature(history.location);
		const unlisten = history.listen((location) => {
			const signature = helpdeskCallRouteSignature(location);
			if (signature === routeSignatureRef.current) {
				return;
			}
			routeSignatureRef.current = signature;
			if (eligibleRef.current && queueCountRef.current > 0) {
				setOpen(true);
			}
		});
		return () => unlisten();
	}, [history]);

	const handleDismiss = useCallback(() => {
		setOpen(false);
	}, []);

	const handleOpenLogCall = useCallback((notification) => {
		const engineerPkey = getEngineerPkey(notification);
		if (engineerPkey == null || Number.isNaN(engineerPkey)) {
			return;
		}
		setActiveEngineer({
			engineer_pkey: engineerPkey,
			engineer_name: displayEngineerName(notification),
			last_contacted: getLastContacted(notification),
			notes: '',
		});
		setLogCallOpen(true);
	}, []);

	const handleCloseLogCall = useCallback(() => {
		setLogCallOpen(false);
		setActiveEngineer(null);
	}, []);

	const handleAfterSave = useCallback(() => {
		if (activeEngineer?.engineer_pkey != null) {
			clearHelpdeskCallSnooze(activeEngineer.engineer_pkey);
		}
		handleCloseLogCall();
		notifyHelpdeskContactedUpdated(activeEngineer?.engineer_pkey);
		if (typeof onSessionCheck === 'function') {
			setTimeout(() => onSessionCheck(), 500);
		}
	}, [activeEngineer, handleCloseLogCall, onSessionCheck]);

	return (
		<>
			<Modal
				isOpen={open && !logCallOpen}
				toggle={() => {}}
				backdrop="static"
				keyboard={false}
				className="helpdesk-call-modal"
				zIndex={1300}
			>
				<ModalHeader>Calls to make</ModalHeader>
				<ModalBody>
					<p className="mb-3">
						These field engineers are overdue for a helpdesk call. Log the call after you make it.
						Dismiss hides this list only until you open another page.
					</p>
					<div className="helpdesk-call-modal-table">
						<table className="table table-sm mb-0">
							<thead>
								<tr>
									<th>Field engineer</th>
									<th>Phone Number</th>
									<th>Last contacted</th>
									<th />
								</tr>
							</thead>
							<tbody>
								{queue.map((notification, index) => {
									const engineerPkey = getEngineerPkey(notification);
									const engineerName = displayEngineerName(notification).trim().toLowerCase();
									const phone = getEngineerPhone(notification)
										|| (engineerPkey != null ? phonesByEngineer[engineerPkey] : '')
										|| phonesByName[engineerName]
										|| '';
									const phoneLabel = phone ? formatPhoneNumber(phone) : '';
									const phoneHref = phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : '';
									return (
									<tr key={getCallRowKey(notification, index)}>
										<td>{displayEngineerName(notification)}</td>
										<td className="text-nowrap">
											{phoneLabel
												? <a href={phoneHref}>{phoneLabel}</a>
												: '—'}
										</td>
										<td>{displayLastContacted(notification)}</td>
										<td className="text-right">
											{engineerPkey == null || Number.isNaN(engineerPkey) ? (
												<span>Missing engineer on this reminder.</span>
											) : (
												<Button
													size="sm"
													color="danger"
													onClick={() => handleOpenLogCall(notification)}
												>
													Log Call
												</Button>
											)}
										</td>
									</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={handleDismiss}>Dismiss</Button>
				</ModalFooter>
			</Modal>

			<Framework.Elements.UniversalModal
				name={`Log Call - ${activeEngineer?.engineer_name ?? 'Field Engineer'}`}
				object={Objects.HelpDesksContacted}
				history={history}
				toggle={logCallOpen}
				reportClose={handleCloseLogCall}
				afterSave={handleAfterSave}
				objectProps={{
					current_note: activeEngineer ?? {engineer_pkey: null, notes: ''},
					date_contacted: moment.tz('America/Phoenix').format('YYYY-MM-DD'),
				}}
				hideGoTo={true}
				modalProps={{zIndex: 1400}}
			/>
		</>
	);
};

export default HelpdeskCallModal;
